import { useContext, useEffect, useReducer, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { getUser } from '@fs/zion-user'
import { getJobs, getTools, getUsers } from '../../../../services/toolsService'
import { PermissionsContext } from '../../../app/PermissionsContext'
import { getIdElement } from '../../../../util/StringUtil'
import useNotifications from '../../../../hooks/useNotifications'

export default () => {}
// Constants
export const actions = {
  SET_CURRENT_USER: 'SET_CURRENT_USER',
  SET_DATS_LINKS: 'SET_DATS_LINKS',
  SET_JOBS_TABLE: 'SET_JOBS_TABLE',
  SET_FILTER_STATUS_LIST: 'SET_FILTER_STATUS_LIST',
  SET_FILTER_CIS_ID: 'SET_FILTER_CIS_ID',
  SET_FILTER_TOOL_NAME: 'SET_FILTER_TOOL_NAME',
  SET_FILTER_GROUP_NAME: 'SET_FILTER_GROUP_NAME',
  SET_OPTIONS_TOOLS: 'SET_OPTIONS_TOOLS',
  SET_DATS_USERS: 'SET_DATS_USERS',
  CLEAR_FILTERS: 'CLEAR_FILTERS',
}

export const jobsRequiringRole = [
  {
    groupName: 'Reservations',
    jobName: 'Remove Reservation',
    roles: ['Data Admin 4', 'Data Admin 5', 'Data Admin 6', 'Russian Data Admin'],
  },
  {
    groupName: 'Reservations',
    jobName: 'Assign to Church',
    roles: ['Data Admin 4', 'Data Admin 5', 'Data Admin 6', 'Russian Data Admin'],
  },
  {
    groupName: 'Reservations',
    jobName: 'Transfer Reservations',
    roles: ['Data Admin 6'],
  },
  {
    groupName: 'Tree Foundation',
    jobName: 'Make Living',
    roles: ['Engineer Admin', 'Data Admin 6'],
  },
]

export function useDatsJobs() {
  const { isRole } = useContext(PermissionsContext)
  const { t } = useTranslation()
  const { error } = useNotifications()
  const isMountedRef = useRef(true)
  const jobStatusOptions = [
    {
      label: t('tools.jobStatus.WaitingForInputFile', 'Waiting For Input File'),
      value: 'WaitingForInputFile',
    },
    {
      label: t('tools.jobStatus.UploadingInputFile', 'Uploading Input File'),
      value: 'UploadingInputFile',
    },
    {
      label: t('tools.jobStatus.ParsingInputFile', 'Parsing Input File'),
      value: 'ParsingInputFile',
    },
    {
      label: t('tools.jobStatus.Running', 'Running'),
      value: 'Running',
    },
    {
      label: t('tools.jobStatus.EnqueuingRequests', 'Enqueuing Requests'),
      value: 'EnqueuingRequests',
    },
    {
      label: t('tools.jobStatus.Paused', 'Paused'),
      value: 'Paused',
    },
    {
      label: t('tools.jobStatus.RecordingTimedOutResults', 'Recording Timed Out Results'),
      value: 'RecordingTimedOutResults',
    },
    {
      label: t('tools.jobStatus.ReadyToConsolidateResultsStep', 'Ready To Consolidate Results Step'),
      value: 'ReadyToConsolidateResultsStep',
    },
    {
      label: t('tools.jobStatus.ConsolidatingResults', 'Consolidating Results'),
      value: 'ConsolidatingResults',
    },
    {
      label: t('tools.jobStatus.Aborted', 'Aborted'),
      value: 'Aborted',
    },
    {
      label: t('tools.jobStatus.Finished', 'Finished'),
      value: 'Finished',
    },
  ]

  // Add Include Object Database option only for Engineer Admin
  if (isRole('Engineer Admin')) {
    jobStatusOptions.push({
      label: t('tools.jobStatus.IncludeObjectDatabase', 'Include Object Database'),
      value: 'IncludeObjectDatabase',
    })
  }

  // State
  const initialState = {
    clearFiltersToggle: false,
    filtersOptions: {
      jobStatusOptions,
      cisIdOptions: [],
      toolGroupOptions: [],
      toolNameOptions: [],
    },
    currentUser: {},
    idsMetadata: {},
    datsUsers: [],
    filters: {
      start: 0,
      count: 30,
      jobStatusList: [],
      cisIdSelectedItem: null,
      groupSelectedItem: undefined,
      toolSelectedItem: undefined,
    },
    jobsData: {},
  }
  // Reducer
  function datsJobsReducer(oldState, action) {
    const newState = { ...oldState }
    const type = action.type
    switch (type) {
      case actions.SET_CURRENT_USER:
        newState.currentUser = action.payload
        // Don't set cisIdSelectedItem here - it will be properly set in SET_DATS_USERS with the full option object
        // This avoids Downshift controlled/uncontrolled prop warnings from object instance changes
        break
      case actions.SET_JOBS_TABLE:
        newState.jobsData = action.payload
        break
      case actions.SET_FILTER_STATUS_LIST:
        newState.filters = {
          ...oldState.filters,
          jobStatusList: action.payload.map((chip) => chip.value),
        }
        break
      case actions.SET_FILTER_CIS_ID:
        newState.filters = { ...oldState.filters, cisIdSelectedItem: action.payload }
        break
      case actions.SET_FILTER_TOOL_NAME:
        newState.filters = { ...oldState.filters, toolSelectedItem: action.payload }
        break
      case actions.SET_FILTER_GROUP_NAME:
        newState.filters = { ...oldState.filters, groupSelectedItem: action.payload }
        break
      case actions.SET_OPTIONS_TOOLS:
        newState.filtersOptions = {
          ...oldState.filtersOptions,
          ...buildToolOptions(action.payload, isRole),
        }
        break
      case actions.SET_DATS_USERS:
        newState.datsUsers = action.payload
        // Deep copy to avoid mutating original
        newState.filtersOptions = {
          ...oldState.filtersOptions,
          cisIdOptions: [],
        }
        setMetadataAndCisIdOptions(newState, action.payload, newState.currentUser.cisId)
        break
      case actions.CLEAR_FILTERS: {
        // Find the current user's option to reset cisIdSelectedItem
        const currentUserOption = oldState.filtersOptions.cisIdOptions.find(
          (option) => option.cisId === oldState.currentUser.cisId
        )
        newState.filters = {
          ...oldState.filters,
          jobStatusList: [],
          cisIdSelectedItem: currentUserOption || null,
          groupSelectedItem: undefined,
          toolSelectedItem: undefined,
        }
        newState.clearFiltersToggle = !oldState.clearFiltersToggle
        break
      }
      default:
    }
    return newState
  }
  // Reducer Hook
  const [datsJobsState, datsJobsDispatch] = useReducer(datsJobsReducer, initialState)
  // Functions that get used in many places
  const datsJobsFunctions = {
    dispatch: datsJobsDispatch,
    getTableFiltered: () => {
      // Check if Include Object Database is selected
      const includeObjectDatabase = datsJobsState.filters?.jobStatusList?.includes('IncludeObjectDatabase')
      // Filter out IncludeObjectDatabase from actual job status list since it's not a real status
      const actualJobStatusList = datsJobsState.filters?.jobStatusList?.filter(
        (status) => status !== 'IncludeObjectDatabase'
      )

      return getJobs(
        datsJobsState.filters.start,
        datsJobsState.filters.count,
        actualJobStatusList,
        datsJobsState.filters.cisIdSelectedItem?.cisId || datsJobsState.currentUser.cisId,
        datsJobsState.filters?.groupSelectedItem?.primaryText,
        datsJobsState.filters?.toolSelectedItem?.primaryText,
        includeObjectDatabase
      )
        .then((jobsData) => {
          if (isMountedRef.current) {
            const action = {
              type: actions.SET_JOBS_TABLE,
              payload: jobsData,
            }
            datsJobsFunctions.dispatch(action)
          }
        })
        .catch((err) => {
          if (isMountedRef.current) {
            error(t('tools.dats.jobsError', 'Error getting jobs: {error}', { error: err.message }))
          }
        })
    },
  }
  // Get stuff asynchronously to get the state ready
  useEffect(() => {
    async function asyncFunction() {
      try {
        // eslint-disable-next-line no-unused-vars
        const [user, toolsData, usersData] = await Promise.all([getUser(), getTools(), getUsers()])
        if (!isMountedRef.current) return

        // datsJobsFunctions.dispatch({
        //   type: actions.SET_CURRENT_USER,
        //   payload: user,
        // })

        datsJobsFunctions.dispatch({
          type: actions.SET_OPTIONS_TOOLS,
          payload: toolsData.tools,
        })

        datsJobsFunctions.dispatch({
          type: actions.SET_DATS_USERS,
          payload: usersData,
        })
      } catch (err) {
        if (isMountedRef.current) {
          error(t('tools.dats.dataError', 'Error loading data: {error}', { error: err.message }))
        }
      }
    }
    asyncFunction()
    return () => {
      isMountedRef.current = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return { datsJobsState, datsJobsFunctions }
}

/**
 * Builds tool name and group filter options from tools data
 * @param {Array} tools - Array of tool objects
 * @param {Function} isRole - Function to check user roles
 * @returns {{toolNameOptions: Array, toolGroupOptions: Array}} Filter options for tools and groups
 */
function buildToolOptions(tools, isRole) {
  const toolNameOptions = []
  const toolGroupOptions = []
  const seenGroups = new Set()

  tools.forEach((tool, index) => {
    let shouldShowJob = true
    if (isRole('Data Admin 4')) {
      shouldShowJob = false
    }
    const jobRequiringRoles = jobsRequiringRole.find((jobRequiringRole) => jobRequiringRole.jobName === tool.name)
    if (jobRequiringRoles) {
      if (!isRole(jobRequiringRoles.roles)) {
        shouldShowJob = false
      } else {
        shouldShowJob = true
      }
    }
    if (shouldShowJob) {
      const toolSuggestionItem = {
        key: index,
        group: tool.groupName,
        primaryText: tool.name,
      }
      toolNameOptions.push(toolSuggestionItem)
      if (!seenGroups.has(tool.groupName)) {
        const groupSuggestionItem = {
          key: toolGroupOptions.length + 1,
          primaryText: tool.groupName,
        }
        toolGroupOptions.push(groupSuggestionItem)
        seenGroups.add(tool.groupName)
      }
    }
  })

  return { toolNameOptions, toolGroupOptions }
}

function setMetadataAndCisIdOptions(newState, datsUsers, currentUserCisId) {
  // Build idsMetadata and cisIdOptions from datsUsers
  newState.idsMetadata = {}
  newState.filtersOptions.cisIdOptions = []
  newState.filters.cisIdSelectedItem = null

  datsUsers.forEach((user, index) => {
    newState.idsMetadata[user.userId] = {
      id: user.userId,
      contactName: user.fullName,
    }
    const cisIdOption = {
      key: index,
      cisId: user.userId,
      primaryText: `${user.fullName} (${getIdElement(user.userId)})`,
    }
    newState.filtersOptions.cisIdOptions.push(cisIdOption)

    // Select the current user's option
    if (user.userId === currentUserCisId) {
      newState.filters.cisIdSelectedItem = cisIdOption
    }
  })
}
